import { useMemo, useCallback } from 'react';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';

import { paths } from 'src/routes/paths';

import { logger } from 'src/utils/logger';
import { refreshAccessToken } from 'src/utils/axios';
import { signOutJsrSession } from 'src/utils/jsr-auth';

import useAppLoading from 'src/stores/loading';
import useUserSessionStore from 'src/stores/user-session';
import oneviewLogo from 'src/theme/themes/oneview/logo.png';
import infosyncLogo from 'src/theme/themes/infosync/logo.png';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { usePopover, CustomPopover } from 'src/components/custom-popover';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export function TenantsPopover() {
  const mediaQuery = 'sm';

  const popover = usePopover();
  const { startAppLoading } = useAppLoading();
  const { user } = useAuthContext();
  const { sessions, activeSessionId, switchSession } = useUserSessionStore();

  const currentSessionId = useMemo(() => activeSessionId, [activeSessionId]);

  const getThemeLogo = useCallback((themeName) => {
    if (!themeName) return null;
    const normalized = String(themeName).toLowerCase();
    if (normalized === 'infosync') return infosyncLogo;
    if (normalized === 'oneview') return oneviewLogo;
    return null;
  }, []);

  const tenants = useMemo(() => {
    if (!user?.username) return [];

    const sameUserSessions = sessions.filter(
      (session) => session?.user?.username === user?.username
    );

    const uniqueTenants = new Map(
      sameUserSessions.map((session) => {
        const tenantId = session?.user?.tenantId || session?.user?.tenant_id || session?.sessionId;
        const tenantName = session?.user?.clientName || session?.user?.tenantId || session?.user?.tenant_id || session?.user?.username;

        const themeLogo = getThemeLogo(session?.user?.theme);

        return [
          tenantId,
          {
            id: session.sessionId,
            sessionId: session.sessionId,
            name: tenantName,
            logo: themeLogo || session?.user?.logo || session?.user?.photoURL,
            plan: session?.user?.plan,
          },
        ];
      })
    );

    return Array.from(uniqueTenants.values());
  }, [getThemeLogo, sessions, user?.username]);

  const handleChangeTenant = useCallback(
    async (tenant) => {
      if (tenant.id === currentSessionId) {
        popover.onClose();
        return;
      }

      startAppLoading();

      await signOutJsrSession({ serverUrl: user?.jsrIntegration?.serverUrl });

      switchSession(tenant.sessionId);
      popover.onClose();

      // switchSession clears accessToken so the old tenant JWT is not sent; we must
      // mint a new access token for the active session (same as account-drawer session switch).
      try {
        await refreshAccessToken();
      } catch (e) {
        logger.warn('Failed to refresh session after tenant switch; reloading', e);
      }

      window.location.href = paths.root;
    },
    [currentSessionId, popover, startAppLoading, switchSession, user?.jsrIntegration?.serverUrl]
  );

  return (
    <>
      <ButtonBase
        disableRipple
        onClick={popover.onOpen}
        sx={{
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
        }}
      >

        <Box
          alt={user?.name}
          sx={{ width: 24, height: 24 }}
        >
          <Iconify icon="mi:switch" width={20} sx={{ color: 'text.secondary' }} />
        </Box>

        <Box
          component="span"
          sx={{
            typography: 'subtitle2',
            display: { xs: 'none', [mediaQuery]: 'inline-flex' },
          }}
        >
          {user?.clientName}
        </Box>

        <Iconify width={16} icon="carbon:chevron-sort" />
      </ButtonBase>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{ arrow: { placement: 'top-left' } }}
      >
        <MenuList sx={{ width: 240 }}>
          {tenants.map((tenant) => (
            <MenuItem
              key={tenant.id}
              selected={tenant.id === currentSessionId}
              onClick={() => handleChangeTenant(tenant)}
              sx={{ height: 48, gap: 1.5 }}
            >
              <Avatar alt={tenant.name} src={tenant.logo} sx={{ width: 24, height: 24 }} />

              <Box component="span" sx={{ flexGrow: 1 }}>
                {tenant.name}
              </Box>

              {tenant.plan && (
                <Label color={tenant.plan === 'Free' ? 'default' : 'info'}>{tenant.plan}</Label>
              )}
            </MenuItem>
          ))}
        </MenuList>
      </CustomPopover>
    </>
  );
}
